/* eslint-disable react/prop-types */
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

/**
 * EntityWizard — Phase 6 foundation component.
 *
 * Multi-step inline creation flow.  NEVER renders as a modal (Trust
 * Contract Rule 2).  Replaces the long "single tall scroll" form
 * pattern.
 *
 * Each step is a `{ key, label, render, validate? }` object.  `render`
 * receives `{ form, patch, setField, errors }` and returns the step's
 * JSX.  `validate` is async, optional, returns `{ ok: bool, errors:
 * {field: msg} }` — when present and `ok=false`, "Next" stays
 * disabled until the user fixes the errors.
 *
 * State persistence
 * -----------------
 *  • `autosaveKey` (optional) — if provided, every form patch is
 *    persisted to localStorage under that key.  On mount, the wizard
 *    seeds itself from that key if present.  Power loss / refresh
 *    proof for long creation flows like new customer / new sales
 *    document.
 *
 * Submit
 * ------
 *  • `onSubmit(form)` runs at the final step's primary action.
 *    Returns a promise.  The wizard renders a loader during the
 *    promise and a green check on success; on failure it shows a
 *    toast + re-enables the submit button.
 *
 * Props
 * -----
 *  title              : "New Job Card"
 *  steps              : [{ key, label, render(props), validate? }]
 *  initialForm        : starting form state
 *  autosaveKey        : optional localStorage key
 *  onSubmit           : fn(form) → Promise
 *  onCancelTo         : navigate target when the user backs out (default: -1)
 *  submitLabel        : "Create Job Card" (default "Create")
 *
 * Usage
 * -----
 *   <EntityWizard
 *     title="New Job Card"
 *     steps={[
 *       { key: "who", label: "Who", render: WhoStep, validate: validateWho },
 *       { key: "what", label: "What", render: WhatStep },
 *       { key: "review", label: "Review", render: ReviewStep },
 *     ]}
 *     initialForm={{ customer_id: "", title: "" }}
 *     autosaveKey="jc:new"
 *     onSubmit={createJobCard}
 *     submitLabel="Create Job Card"
 *   />
 */
export default function EntityWizard({
  title,
  steps = [],
  initialForm = {},
  autosaveKey = null,
  onSubmit,
  onCancelTo,
  submitLabel = "Create",
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState(() => {
    if (!autosaveKey) return initialForm;
    try {
      const raw = localStorage.getItem(`hv:wizard:${autosaveKey}`);
      if (raw) return { ...initialForm, ...JSON.parse(raw) };
    } catch (_e) { /* noop */ }
    return initialForm;
  });
  const [stepIdx, setStepIdx] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const validatingRef = useRef(false);

  // Auto-save on every form mutation.
  useEffect(() => {
    if (!autosaveKey) return;
    try {
      localStorage.setItem(`hv:wizard:${autosaveKey}`, JSON.stringify(form));
    } catch (_e) { /* quota — drop silently */ }
  }, [form, autosaveKey]);

  const patch = useCallback(
    (partial) => setForm((prev) => ({ ...prev, ...partial })),
    [],
  );
  const setField = useCallback(
    (name, value) => setForm((prev) => ({ ...prev, [name]: value })),
    [],
  );

  const currentStep = steps[stepIdx];
  const isLast = stepIdx === steps.length - 1;
  const isFirst = stepIdx === 0;

  const goNext = useCallback(async () => {
    if (!currentStep) return;
    if (currentStep.validate) {
      validatingRef.current = true;
      try {
        const out = await currentStep.validate(form);
        if (!out?.ok) {
          setErrors(out?.errors || {});
          return;
        }
        setErrors({});
      } finally {
        validatingRef.current = false;
      }
    }
    if (isLast) {
      try {
        setSubmitting(true);
        await onSubmit?.(form);
        if (autosaveKey) {
          try { localStorage.removeItem(`hv:wizard:${autosaveKey}`); } catch (_e) { /* noop */ }
        }
        setSubmitted(true);
      } catch (err) {
        toast.error(err?.message || "Could not save — please try again");
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setStepIdx((i) => Math.min(i + 1, steps.length - 1));
  }, [currentStep, form, isLast, onSubmit, autosaveKey, steps.length]);

  const goBack = useCallback(() => {
    if (isFirst) {
      onCancelTo ? navigate(onCancelTo) : navigate(-1);
      return;
    }
    setStepIdx((i) => Math.max(i - 1, 0));
  }, [isFirst, navigate, onCancelTo]);

  const progress = useMemo(() => {
    if (!steps.length) return 0;
    return Math.round(((stepIdx + (submitted ? 1 : 0)) / steps.length) * 100);
  }, [stepIdx, steps.length, submitted]);

  return (
    <div
      data-testid="entity-wizard"
      className="min-h-screen bg-appbg text-slate-100"
    >
      {/* ── Sticky header ─────────────────────────────────────────── */}
      <header
        data-testid="entity-wizard-header"
        className="sticky top-0 z-30 border-b border-appline bg-appbg/85 backdrop-blur"
      >
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3 sm:px-6">
          <button
            data-testid="entity-wizard-back-btn"
            onClick={goBack}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-300 hover:bg-slate-800/60 hover:text-slate-100"
            aria-label={isFirst ? "Cancel" : "Previous step"}
          >
            {isFirst ? <ArrowLeft className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wider text-slate-400">
              Step {Math.min(stepIdx + 1, steps.length)} of {steps.length}
              {currentStep?.label ? ` — ${currentStep.label}` : ""}
            </p>
            <h1 data-testid="entity-wizard-title" className="truncate text-lg font-semibold sm:text-xl">
              {title}
            </h1>
          </div>
        </div>
        {/* Progress bar */}
        <div
          data-testid="entity-wizard-progress"
          className="mx-auto h-1 max-w-3xl bg-slate-800"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-1 bg-cyan-400 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </header>

      {/* ── Step body ─────────────────────────────────────────────── */}
      <main
        data-testid="entity-wizard-body"
        className="mx-auto max-w-3xl space-y-4 px-3 py-4 sm:space-y-6 sm:px-6 sm:py-6"
      >
        {submitted ? (
          <div
            data-testid="entity-wizard-done"
            className="rounded-2xl border border-emerald-700/40 bg-emerald-900/20 p-6 text-center"
          >
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300">
              <Check className="h-7 w-7" />
            </div>
            <p className="mt-3 text-base font-medium text-emerald-200">Saved.</p>
          </div>
        ) : currentStep?.render ? (
          // [Iter 290i] CRITICAL — Render the step body as a proper
          // React component, NEVER as a function call. Each step (e.g.
          // WhoStep) has its own useState/useEffect hooks. If we call
          // `currentStep.render(props)` inline, those hooks attach to
          // EntityWizard's render and the hook count CHANGES on every
          // stepIdx transition (WhoStep has 4 hooks, WhatStep has 0),
          // which trips React error #310 ("Rendered fewer hooks than
          // expected"). In production minified builds this surfaces
          // as React error #300. Solution: assign to a Capitalized
          // variable so React treats it as a Component — its hooks
          // get their own component instance and don't pollute the
          // parent's hook list.
          (() => {
            const StepComponent = currentStep.render;
            return (
              <StepComponent
                form={form}
                patch={patch}
                setField={setField}
                errors={errors}
              />
            );
          })()
        ) : (
          <p className="text-slate-400">Step has no body.</p>
        )}
      </main>

      {/* ── Sticky footer actions ─────────────────────────────────── */}
      {!submitted && (
        <footer
          data-testid="entity-wizard-footer"
          className="sticky bottom-0 z-30 border-t border-appline bg-appbg/95 backdrop-blur"
        >
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
            <button
              data-testid="entity-wizard-cancel-btn"
              onClick={goBack}
              disabled={submitting}
              className="flex h-11 items-center gap-1 rounded-full px-4 text-sm text-slate-300 hover:bg-slate-800/60 disabled:opacity-40"
            >
              {isFirst ? "Cancel" : (<><ChevronLeft className="h-4 w-4" />Back</>)}
            </button>
            <button
              data-testid="entity-wizard-next-btn"
              onClick={goNext}
              disabled={submitting}
              className={cn(
                "flex h-11 items-center gap-2 rounded-full px-5 text-sm font-medium",
                "bg-cyan-500 text-slate-950 hover:bg-cyan-400 disabled:opacity-50",
              )}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isLast ? submitLabel : (<>Next<ChevronRight className="h-4 w-4" /></>)}
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}
